import * as React from "react";
import { Tooltip } from "@mui/material";
import { cva, type VariantProps } from "class-variance-authority";
import { Badge } from "./badge";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

const chipVariants = cva("rounded-2xl", {
  variants: {
    size: {
      small: "px-1.5 py-0.5 text-xs",
      medium: "px-2 py-0.5 text-xs",
      large: "px-3 py-1 text-sm",
    },
    clickable: {
      true: "cursor-pointer hover:opacity-80 transition-opacity",
      false: "",
    },
    disabled: {
      true: "opacity-50 cursor-not-allowed pointer-events-none",
      false: "",
    },
  },
  defaultVariants: {
    size: "medium",
    clickable: false,
    disabled: false,
  },
});

interface ChipProps
  extends React.ComponentProps<typeof Badge>,
  VariantProps<typeof chipVariants> {
  label: string;
  size?: "small" | "medium" | "large";
  clickable?: boolean;
  disabled?: boolean;
  onDelete?: () => void;
  deleteTooltip?: React.ReactNode;
}

const getButtonSize = (size: string) => {
  const sizes = {
    small: "h-3 w-3",
    medium: "h-3.5 w-3.5",
    large: "h-4 w-4"
  };
  return sizes[size as keyof typeof sizes] || sizes.medium;
};

const getIconSize = (size: string) => {
  const sizes = {
    small: "h-2 w-2",
    medium: "h-2.5 w-2.5",
    large: "h-3 w-3"
  };
  return sizes[size as keyof typeof sizes] || sizes.medium;
};

const DeleteButton = ({
  size,
  disabled,
  onDelete
}: {
  size: string;
  disabled: boolean;
  onDelete: () => void;
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onDelete();
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        "ml-1 inline-flex items-center justify-center rounded-full",
        "hover:bg-black/10 dark:hover:bg-white/10 transition-colors",
        "focus:outline-none focus:ring-1 focus:ring-current cursor-pointer",
        getButtonSize(size),
        disabled && "pointer-events-none"
      )}
      aria-label="Remove"
    >
      <X className={getIconSize(size)} />
    </button>
  );
};

function Chip({
  label,
  size,
  clickable = false,
  disabled = false,
  onDelete,
  deleteTooltip,
  className,
  variant,
  asChild = false,
  onClick,
  ...props
}: ChipProps) {
  const isClickable = clickable && !disabled;

  return (
    <Badge
      variant={variant}
      asChild={asChild}
      className={cn(
        chipVariants({ size, clickable: isClickable, disabled }),
        onDelete && "pr-1",
        className
      )}
      onClick={isClickable ? onClick : undefined}
      role={clickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : -1}
      aria-disabled={disabled}
      {...props}
    >
      <span className="flex items-center gap-1">
        {label}
        {onDelete && deleteTooltip && (
          <Tooltip title={deleteTooltip} placement="top" arrow>
            <span className="inline-flex">
              <DeleteButton
                size={size || "medium"}
                disabled={disabled}
                onDelete={onDelete}
              />
            </span>
          </Tooltip>
        )}
        {onDelete && !deleteTooltip && (
          <DeleteButton
            size={size || "medium"}
            disabled={disabled}
            onDelete={onDelete}
          />
        )}
      </span>
    </Badge>
  );
}

export { Chip, chipVariants };
export type { ChipProps };
